import { Fragment, useMemo, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { Panel, RoutedPanelProps } from '@/components/Layout/Panel/Panel';
import { Loader2, AlertCircle, Car, Clock, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  useMyDayWorkorders,
  useRoutes,
  sortWorkorders,
  useGeocodedCoordinates,
} from './myday.hooks';
import {
  useWorkorderRouteErrors,
  useRoutingKeyStatus,
  useOptimizeRouteMutation,
} from '../dayplanning/dayplanning.hooks';
import { WorkorderNavigation } from '../myworkorders/components/WorkorderNavigation';
import { ViewModeToggle } from '../myworkorders/components/ViewModeToggle';
import { DayPlanningMap } from '../dayplanning/components/DayPlanningMap';
import { RouteStatusBadge } from '../dayplanning/components/RouteStatusBadge';
import { ReadOnlyRouteSegment } from './components/ReadOnlyRouteSegment';
import { UserSwitcher } from './components/UserSwitcher';
import { MapViewToggle } from '../components/MapViewToggle';
import { HQRouteSegment } from '../components/HQRouteSegment';
import {
  useRouteResolution,
  useRouteTotals,
  countFailedRoutes,
  hasRateLimitError,
  useAutoOptimization,
  WorkorderCard,
  MapErrorBoundary,
} from '../shared';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { usePanelState } from '@/app/navigation/usePanelState';
import type { MyWorkordersView } from '@/features/user-preferences/user-preferences.hooks';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface MyDayPanelProps extends RoutedPanelProps {
  mode?: MyWorkordersView;
  onModeChange?: (mode: MyWorkordersView) => void;
}

export const MyDayPanel = ({
  params,
  path,
  mode = 'day',
  onModeChange,
  ...panelProps
}: MyDayPanelProps) => {
  const date = params.date || format(new Date(), 'yyyy-MM-dd');

  // Use panel state to persist selected user across navigation
  const [panelState, setPanelState] = usePanelState<{ userId?: string }>();
  const userId = panelState.userId ? parseInt(panelState.userId, 10) : null;

  const { data: workorders, isLoading: isLoadingWorkorders } =
    useMyDayWorkorders(userId, date);

  const sorted = useMemo(() => sortWorkorders(workorders ?? []), [workorders]);

  const { data: routes, isLoading: isLoadingRoutes, error } = useRoutes(sorted);
  const { data: geocodedCoords } = useGeocodedCoordinates(sorted);
  const [showMap, setShowMap] = useLocalStorage('myday.showMap', true);

  // Get route errors per workorder
  const routeErrors = useWorkorderRouteErrors(sorted, routes);

  // Route resolution (handles via_hq complexity) - use shared hook
  const { getRouteForWorkorder, leadingRoute, trailingRoute } =
    useRouteResolution(sorted, routes);

  // Route totals and failed routes count
  const routeTotals = useRouteTotals(routes);
  const failedRoutesCount = countFailedRoutes(routes);
  const hasRateLimit = hasRateLimitError(routes);

  // Route ordering clarity hooks
  const routingStatus = useRoutingKeyStatus(sorted);

  // Get effective user ID for route operations (selected user or first workorder's assigned user)
  const routeUserId = userId ?? sorted[0]?.assigned_user_id ?? undefined;

  const optimizeMutation = useOptimizeRouteMutation(
    date,
    routeUserId ?? undefined,
  );

  // Use shared auto-optimization hook
  const { handleMutation, handleOptimizeRoute, willAutoOptimize } =
    useAutoOptimization({
      routingStatus,
      optimizeMutation,
      routeUserId: routeUserId ?? undefined,
      workorderCount: sorted.length,
    });

  useEffect(() => {
    if (error) {
      toast.error('Could not load routes', {
        description: error.message || String(error),
      });
    }
  }, [error]);

  // Handler for user selection changes
  const handleUserChange = useCallback(
    (newUserId: number | null) => {
      setPanelState({ userId: newUserId?.toString() });
    },
    [setPanelState],
  );

  return (
    <Panel {...panelProps} title="My Workorders">
      {/* Navigation - sticky header */}
      <div className="bg-card sticky top-[41px] z-[5] space-y-2 pb-2 md:top-[49px]">
        <WorkorderNavigation date={date} mode="day" />
        <div className="flex items-center justify-between gap-2">
          <UserSwitcher
            selectedUserId={userId}
            onUserChange={handleUserChange}
          />
          <div className="flex items-center gap-2">
            {onModeChange && (
              <ViewModeToggle mode={mode} onChange={onModeChange} />
            )}
            <MapViewToggle showMap={showMap} onToggleMap={setShowMap} />
          </div>
        </div>
      </div>

      {isLoadingWorkorders ? (
        <div
          className="flex items-center justify-center py-12"
          role="status"
          aria-live="polite"
        >
          <Loader2
            className="text-muted-foreground h-8 w-8 animate-spin"
            aria-hidden="true"
          />
          <span className="sr-only">Loading workorders...</span>
        </div>
      ) : sorted.length === 0 ? (
        <div className="text-muted-foreground py-12 text-center">
          No workorders scheduled for this day
        </div>
      ) : (
        <div
          className={cn(
            'flex flex-col gap-4',
            showMap && '@lg:grid @lg:grid-cols-2',
          )}
        >
          {/* Map */}
          {showMap && (
            <div
              className={cn(
                'isolate',
                'order-first @lg:order-last',
                '@lg:sticky @lg:top-0 @lg:self-start',
              )}
            >
              <MapErrorBoundary
                resetKey={`${date}-${userId}-${sorted.length}`}
                fallbackClassName="h-64 @lg:h-[calc(100vh-18rem)] @lg:min-h-128"
              >
                <DayPlanningMap
                  workorders={sorted}
                  routes={routes}
                  geocodedCoords={geocodedCoords}
                  className="h-64 @lg:h-[calc(100vh-18rem)] @lg:min-h-128"
                />
              </MapErrorBoundary>
            </div>
          )}

          {/* List content */}
          <div>
            {/* Optimization error alert */}
            {optimizeMutation.isError && (
              <div
                role="alert"
                className="mb-4 flex items-center justify-between gap-3 rounded-md border border-red-500/50
                  bg-red-50 px-4 py-3 text-sm text-red-800 dark:bg-red-950/30 dark:text-red-200"
              >
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  <span>
                    {optimizeMutation.error instanceof Error
                      ? optimizeMutation.error.message
                      : 'Route optimization failed. Please try again.'}
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleOptimizeRoute}
                  disabled={optimizeMutation.isPending}
                  className="shrink-0"
                >
                  <RefreshCw
                    className={cn(
                      'mr-1 h-4 w-4',
                      optimizeMutation.isPending && 'animate-spin',
                    )}
                  />
                  Retry
                </Button>
              </div>
            )}

            {/* Route summary: status badge + totals */}
            {sorted.length > 0 && (
              <div className="mb-4 flex flex-wrap items-center gap-3">
                <RouteStatusBadge
                  status={routingStatus.status}
                  canAutoSort={routingStatus.canAutoSort}
                  canManualEdit={routingStatus.canManualEdit}
                  onOptimize={handleOptimizeRoute}
                  isOptimizing={optimizeMutation.isPending || willAutoOptimize}
                  routingTimestamp={routingStatus.routingTimestamp}
                />

                {/* Route totals */}
                {routeTotals && !isLoadingRoutes && (
                  <div className="text-muted-foreground flex items-center gap-3 text-sm">
                    <span className="flex items-center gap-1">
                      <Car className="h-4 w-4" />
                      {routeTotals.distanceStr}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {routeTotals.durationStr}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Rate limit error alert */}
            {hasRateLimit && (
              <div
                role="alert"
                className="mb-4 flex items-center justify-between gap-3 rounded-md border border-red-500/50
                  bg-red-50 px-4 py-3 text-sm text-red-800 dark:bg-red-950/30 dark:text-red-200"
              >
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  <span>
                    Route calculation rate limited. Some routes could not be
                    calculated. Please wait a moment and try again.
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.location.reload()}
                  className="shrink-0"
                >
                  <RefreshCw className="mr-1 h-4 w-4" />
                  Reload
                </Button>
              </div>
            )}

            {/* Warning for failed routes */}
            {failedRoutesCount > 0 && !hasRateLimit && (
              <div
                role="status"
                className="mb-4 flex items-center gap-2 rounded-md border border-amber-500/50 bg-amber-50
                  px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
              >
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>
                  {failedRoutesCount}{' '}
                  {failedRoutesCount === 1 ? 'workorder' : 'workorders'}{' '}
                  excluded from route calculation (incomplete address data)
                </span>
              </div>
            )}

            {/* Workorder cards - read-only, no drag-drop */}
            <div className="space-y-2 pb-4 sm:space-y-1">
              {/* Leading route: HQ -> first workorder */}
              <HQRouteSegment
                route={leadingRoute}
                direction="from_hq"
                isLoading={isLoadingRoutes}
              />

              {sorted.map((wo, idx) => (
                <Fragment key={wo.recnum}>
                  {/* Route segment (shown between cards, not before first) */}
                  {idx > 0 && (
                    <ReadOnlyRouteSegment
                      route={getRouteForWorkorder(idx) ?? undefined}
                      isLoading={isLoadingRoutes}
                    />
                  )}

                  {/* Workorder card - use 1-based index to match map markers */}
                  <WorkorderCard
                    mode="readonly"
                    workorder={wo}
                    index={idx}
                    routeError={routeErrors.get(wo.recnum)}
                    linkBase={path}
                  />
                </Fragment>
              ))}

              {/* Trailing route: last workorder -> HQ */}
              <HQRouteSegment
                route={trailingRoute}
                direction="to_hq"
                isLoading={isLoadingRoutes}
              />
            </div>
          </div>
        </div>
      )}
    </Panel>
  );
};
